import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import {
  getDropdownData,
  getPendingKyc,
  pendingKycSubmit,
  busGetIndBusAtList,
  busGetPendingKyc,
  busKycSubmit,
  kycHistory,
  busKycHistory,
  updateErrorStatus,
  setLinkExpired,
} from "../../redux/slices/EnqMngtSlice";
import { Form, Typography, Checkbox, message, Tag, Flex, Result } from "antd";
import CustomCard from "../../components/CustomCard/CustomCard";
import CustomButton from "../../components/CustomButton/Button";
import CancelButton from "../../components/CustomButton/CancelButton";
import {
  busVerifyBusPan, verifyPan, busVerifyBusGstin, verifyBusGstin
} from "../../redux/slices/EnqMngtSlice";
import KycBusinessDetails from "./Components/KycBusinessDetails";
import { TITLE_TXT, BTN_TXT, ENQUIRY_SUCCESS_MESSAGES } from "../../constants/constants";
import KycAddress from "./Components/KycAddress";
import KycBank from "./Components/KycBank";
import KycAdditionalInfo from "./Components/KycAdditionalInfo";
import "./EnqMngt.scss";
import KycContactDirector from "./Components/KycContactDirector";
import FormFooter from "../../components/CustomForms/FormFooter";
import dayjs from "dayjs";
import KycSuccess from "./Components/KycSuccess";
import {
  isCommunicationSameAsPrimary,
  buildCommunicationAddressPayload,
} from "../../helpers/kycAddress";
import {
  getKycStatusLabel,
  getKycStatusTagColor,
  shouldShowKycSuccessScreen,
  isCompleteKycReadOnly,
  getEffectiveKycStatusName,
  isKycSubmittedStatus,
  isReusableKycConfirmMode,
} from "../../helpers/kycStatus";
import { getBusinessPortalKycRedirectUrl } from "../../helpers/businessPortal";

const { Text } = Typography;

const cleanNa = (val) => {
  if (!val) return "";
  const lower = String(val).toLowerCase();
  return lower === "n/a" ? "" : val;
};

const isTokenExpiredClient = (token) => {
  if (!token) return false;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return false;
    const base64Url = parts[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const payload = JSON.parse(jsonPayload);
    if (payload.exp && Date.now() >= payload.exp * 1000) {
      return true;
    }
    if (payload.lastLoginAt) {
      const loginAt = new Date(payload.lastLoginAt).getTime();
      const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;
      if (!isNaN(loginAt) && Date.now() - loginAt >= TWENTY_FOUR_HOURS_MS) {
        return true;
      }
    }
    return false;
  } catch {
    return false;
  }
};

const buildCompleteKycFormValues = (pendingKycData) => {
  if (!pendingKycData) return {};

  const kyc = pendingKycData.kycDetails || {};
  const bank = pendingKycData.BankDetailsView || {};

  const verifiedPan =
    kyc.kycPanValue?.isVerified && kyc.kycPanValue?.docValue
      ? kyc.kycPanValue.docValue
      : null;
  const verifiedGstin =
    kyc.kycGstinValue?.isVerified && kyc.kycGstinValue?.docValue
      ? kyc.kycGstinValue.docValue
      : null;
  const verifiedBank =
    kyc.kycBank?.isVerified && kyc.kycBank?.docValue
      ? kyc.kycBank.docValue
      : null;
  const verifiedDirAadhaar =
    kyc.kycDirectorsAadhaarValue?.isVerified && kyc.kycDirectorsAadhaarValue?.docValue
      ? kyc.kycDirectorsAadhaarValue.docValue
      : null;
  const verifiedDirPan =
    kyc.kycDirectorsPanValue?.isVerified && kyc.kycDirectorsPanValue?.docValue
      ? kyc.kycDirectorsPanValue.docValue
      : null;

  return {
    busName: pendingKycData.busName,
    busPan: verifiedPan || pendingKycData.pan || kyc.kycPanValue?.docValue,
    busType: pendingKycData.businessTypeId,
    incorporationDate: pendingKycData.incorporationDate
      ? dayjs(pendingKycData.incorporationDate)
      : null,
    industryType: pendingKycData.industryTypeId,
    annualTurnover: pendingKycData.annualTurnoverId,
    hasMultipleGstin: pendingKycData.hasMultipleGstin,
    gstin: verifiedGstin || pendingKycData.gstin || kyc.kycGstinValue?.docValue,
    primaryAdd1: pendingKycData.primaryAddLine1,
    primaryAdd2: pendingKycData.primaryAddLine2,
    primaryCity: pendingKycData.primaryCityName,
    primaryState: pendingKycData.primaryStateName,
    primaryPincode: pendingKycData.primaryZipCode,
    commAdd1: cleanNa(pendingKycData.billingAddLine1),
    commAdd2: cleanNa(pendingKycData.billingAddLine2),
    commCity: cleanNa(pendingKycData.billingCityName),
    commState: cleanNa(pendingKycData.billingStateName),
    commPincode: cleanNa(pendingKycData.billingZipCode),
    commSameAsPrimary: isCommunicationSameAsPrimary(pendingKycData),
    email: pendingKycData.email,
    phoneNo: pendingKycData.contactPhoneNumber,
    ifsc: bank.ifscCode,
    bankName: bank.bankName,
    branchName: bank.branchName,
    accHolderName: cleanNa(bank.accHolderName) || pendingKycData.busName || "",
    // Prefer verified DocArchive bank value (manual KYC). type=number would hide non-numeric ciphertext.
    accNum: verifiedBank || bank.accNum || kyc.kycBank?.docValue,
    tenure: pendingKycData.expectedTenure?.toString(),
    expAmt: pendingKycData.expectedCredit,
    busWebsite: pendingKycData.busWebsite?.replace(/^https?:\/\//i, ""),
    dealType: pendingKycData.dealCategoryId,
    busDesc: pendingKycData.busDescription,
    finSummary: pendingKycData.financialSummary,
    dirName: cleanNa(pendingKycData.directorName),
    dirAadhaar: verifiedDirAadhaar || kyc.kycDirectorsAadhaarValue?.docValue,
    dirPan: verifiedDirPan || kyc.kycDirectorsPanValue?.docValue,
  };
};

const CompleteKyc = () => {
  const [form] = Form.useForm();
  const dispatch = useDispatch();
  const { orgId } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const organizationId = searchParams.get("orgId");
  const navigate = useNavigate();
  const routeDealCategoryId =
    location.state?.dealCategoryId || searchParams.get("dealCategoryId");
  const { pendingKycData, pendingKycSubmitLoading, error, panError, gstinError, panLoading, gstinLoading, kycHistoryData, isLinkExpired } =
    useSelector((state) => state?.enqMngt);
  const isExpired = Boolean(isLinkExpired) || Boolean(token && isTokenExpiredClient(token));
  const formValues = Form.useWatch(undefined, form);
  const {
    busPan,
    busName,
    gstin,
    email,
    phoneNo,
    ifsc,
    accNum,
    accHolderName,
  } = formValues || {};
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [draftLoading, setDraftLoading] = useState(false);
  const mergedKycDetails =
    kycHistoryData?.kycDetails || pendingKycData?.kycDetails || {};
  const isPanVerified = mergedKycDetails?.kycPanValue?.isVerified;
  const isGstinVerified = mergedKycDetails?.kycGstinValue?.isVerified;
  const effectiveStatusName = getEffectiveKycStatusName({
    statusName: pendingKycData?.statusName,
    isSubmitted,
    hasToken: Boolean(token),
  });
  const kycStatusLabel = getKycStatusLabel({
    kycDetails: mergedKycDetails,
    statusName: effectiveStatusName,
  });
  const kycStatusColor = getKycStatusTagColor(kycStatusLabel);
  const showKycSuccess = shouldShowKycSuccessScreen({
    statusName: effectiveStatusName,
    isSubmitted,
    hasToken: Boolean(token),
  });
  const isReadOnly = isCompleteKycReadOnly({
    statusName: effectiveStatusName,
    isSubmitted,
    hasToken: Boolean(token),
  });
  const isReuseConfirmMode = isReusableKycConfirmMode({
    statusName: effectiveStatusName,
    kycDetails: mergedKycDetails,
    isSubmitted,
    hasToken: Boolean(token),
    hasOtherCompletedKyc: Boolean(pendingKycData?.hasOtherCompletedKyc),
  });
  // Prefill + lock fields when reusing IF KYC for SCF; still allow checkbox + submit
  const isFieldReview = isReadOnly || isReuseConfirmMode;
  
  const isAlreadyCompleted = Boolean(
    token &&
    isKycSubmittedStatus(pendingKycData?.statusName) &&
    !isSubmitted
  );

  useEffect(() => {
    if (token) {
      if (isTokenExpiredClient(token)) {
        dispatch(setLinkExpired(true));
        return;
      }
      dispatch(busGetIndBusAtList({ token }));
      dispatch(busGetPendingKyc({ orgId: organizationId, token }));
      dispatch(busKycHistory({ orgId: organizationId, token }));
    } else {
      dispatch(getDropdownData());
      dispatch(
        getPendingKyc({
          orgId,
          dealCategoryId: routeDealCategoryId,
        })
      );
      dispatch(kycHistory(orgId));
    }
    return () => {
      dispatch(updateErrorStatus());
    };
  }, [organizationId, dispatch, token, orgId, routeDealCategoryId]);


  useEffect(() => {
    if (pendingKycData) {
      form.setFieldsValue(buildCompleteKycFormValues(pendingKycData));
    }
  }, [pendingKycData, form]);

  useEffect(() => {
    const directorDetails = kycHistoryData?.kycDetails || pendingKycData?.kycDetails;
    const historyKyc = kycHistoryData?.kycDetails;
    const patch = {};

    if (directorDetails?.kycDirectorsAadhaarValue?.isVerified) {
      patch.dirAadhaar = directorDetails.kycDirectorsAadhaarValue?.docValue;
      patch.dirName =
        kycHistoryData?.directorName || pendingKycData?.directorName;
    }

    if (directorDetails?.kycDirectorsPanValue?.isVerified) {
      patch.dirPan = directorDetails.kycDirectorsPanValue?.docValue;
    }

    if (historyKyc?.kycBank?.isVerified && historyKyc.kycBank?.docValue) {
      patch.accNum = historyKyc.kycBank.docValue;
    }
    const bankView =
      pendingKycData?.BankDetailsView || kycHistoryData?.orgDets?.BankDetailsView;
    if (historyKyc?.kycBank?.isVerified && bankView) {
      if (bankView.ifscCode) patch.ifsc = bankView.ifscCode;
      if (bankView.bankName) patch.bankName = bankView.bankName;
      if (bankView.branchName) patch.branchName = bankView.branchName;
      if (bankView.accHolderName) {
        patch.accHolderName = bankView.accHolderName;
      } else if (pendingKycData?.busName) {
        patch.accHolderName = pendingKycData.busName;
      }
    }
    if (historyKyc?.kycPanValue?.isVerified && historyKyc.kycPanValue?.docValue) {
      patch.busPan = historyKyc.kycPanValue.docValue;
    }
    if (historyKyc?.kycGstinValue?.isVerified && historyKyc.kycGstinValue?.docValue) {
      patch.gstin = historyKyc.kycGstinValue.docValue;
    }

    if (Object.keys(patch).length) {
      form.setFieldsValue(patch);
    }
  }, [kycHistoryData, pendingKycData, form]);

  const handleVerifyBoth = async () => {

    let refreshRequired = false;
    const dealCategoryId =
      formValues?.dealType ||
      pendingKycData?.dealCategoryId ||
      routeDealCategoryId;

    if (!isPanVerified) {
      let panResponse
      if (token) {
        panResponse = await dispatch(
          busVerifyBusPan({
            busName,
            pan: busPan,
            orgId: organizationId,
            token,
            dealCategoryId,
          })
        );
      } else {
        panResponse = await dispatch(
          verifyPan({
            busName,
            pan: busPan,
            orgId,
            dealCategoryId,
          })
        );
      }
      if (panResponse?.payload?.statusCode !== 201) {
        return;
      }

      refreshRequired = true;
    }

    if (!isGstinVerified) {
      let gstinReponse
      if (token) {
        gstinReponse = await dispatch(
          busVerifyBusGstin({
            gstin: gstin,
            orgId: organizationId,
            token,
            dealCategoryId,
          })
        );
      } else {
        gstinReponse = await dispatch(
          verifyBusGstin({
            gstin: gstin,
            orgId,
            dealCategoryId,
          })
        );
      }
      if (gstinReponse?.payload?.statusCode === 201) {
        refreshRequired = true;
      }
    }

    if (refreshRequired) {
      if (token) {
        dispatch(
          busGetPendingKyc({
            orgId: organizationId,
            token,
          })
        );
        dispatch(busKycHistory({ orgId: organizationId, token }));
      } else {
        dispatch(
          getPendingKyc({
            orgId,
            dealCategoryId: routeDealCategoryId,
          })
        );
        dispatch(kycHistory(orgId));
      }
    }
  };

  const handleSubmitKyc = async (values) => {
    const payload = {
      token: token ? token : undefined,
      orgId: token ? organizationId : orgId,
      businessTypeId: values.busType,
      industryTypeId: values.industryType,
      hasMultipleGstin: values.hasMultipleGstin,
      annualTurnoverId: values.annualTurnover,
      dealCategoryId: values.dealType,
      expectedTenure: values.tenure,
      expectedCredit: values.expAmt,
      incorporationDate: values.incorporationDate || pendingKycData.incorporationDate,
      busDescription: values.busDesc,
      website: values.busWebsite
        ? values.busWebsite.match(/^https?:\/\//i)
          ? values.busWebsite
          : `http://${values.busWebsite}`
        : "",
      financialSummary: values.finSummary,
      communicationAddress: buildCommunicationAddressPayload(values),
      // docs: {
      //   kycPanDoc: pendingKycData?.kycDetails?.kycPanDoc?.isVerified
      //     ? null
      //     : uploadedFiles.kycPanDoc,
      //   kycGstinDoc: pendingKycData?.kycDetails?.kycGstinDoc?.isVerified
      //     ? null
      //     : uploadedFiles.kycGstinDoc,
      //   kycDirectorsPanDoc: pendingKycData?.kycDetails?.kycDirectorsPanDoc
      //     ?.isVerified
      //     ? null
      //     : uploadedFiles.kycDirectorsPanDoc,
      //   kycDirectorsAadhaarDoc: pendingKycData?.kycDetails
      //     ?.kycDirectorsAadhaarDoc?.isVerified
      //     ? null
      //     : uploadedFiles.kycDirectorsAadhaarDoc,
      //   kycDirectorsPicture: pendingKycData?.kycDetails?.kycDirectorsPicture
      //     ?.isVerified
      //     ? null
      //     : uploadedFiles.kycDirectorsPicture,
      //   Bank_Statement_12_months: pendingKycData?.kycDetails
      //     ?.Bank_Statement_12_months?.isVerified
      //     ? null
      //     : uploadedFiles.Bank_Statement_12_months,
      //   Audited_Financial_Statement_3_years: pendingKycData?.kycDetails
      //     ?.Audited_Financial_Statement_3_years?.isVerified
      //     ? null
      //     : uploadedFiles.Audited_Financial_Statement_3_years,
      //   Sanction_Letter_existing_borrowings: pendingKycData?.kycDetails
      //     ?.Sanction_Letter_existing_borrowings?.isVerified
      //     ? null
      //     : uploadedFiles.Sanction_Letter_existing_borrowings,
      //   GST_Returns_12_months: pendingKycData?.kycDetails?.GST_Returns_12_months
      //     ?.isVerified
      //     ? null
      //     : uploadedFiles.GST_Returns_12_months,
      //   Provisional_Financial_Statement_latest_year: pendingKycData?.kycDetails
      //     ?.Provisional_Financial_Statement_latest_year?.isVerified
      //     ? null
      //     : uploadedFiles.Provisional_Financial_Statement_latest_year,
      //   ITR_last_2_years: pendingKycData?.kycDetails?.ITR_last_2_years
      //     ?.isVerified
      //     ? null
      //     : uploadedFiles.ITR_last_2_years,
      //   Certificate_of_Incorporation: pendingKycData?.kycDetails
      //     ?.Certificate_of_Incorporation?.isVerified
      //     ? null
      //     : uploadedFiles.Certificate_of_Incorporation,
      //   KYC_Authorized_Signatory: pendingKycData?.kycDetails
      //     ?.KYC_Authorized_Signatory?.isVerified
      //     ? null
      //     : uploadedFiles.KYC_Authorized_Signatory,
      //   Current_Shareholding_Pattern: pendingKycData?.kycDetails
      //     ?.Current_Shareholding_Pattern?.isVerified
      //     ? null
      //     : uploadedFiles.Current_Shareholding_Pattern,
      //   Memorandum_and_Articles: pendingKycData?.kycDetails
      //     ?.Memorandum_and_Articles?.isVerified
      //     ? null
      //     : uploadedFiles.Memorandum_and_Articles,
      //   Constitution_Documents: pendingKycData?.kycDetails
      //     ?.Constitution_Documents?.isVerified
      //     ? null
      //     : uploadedFiles.Constitution_Documents,
      //   Statutory_Reqs_Power_of_Attorney: pendingKycData?.kycDetails
      //     ?.Statutory_Reqs_Power_of_Attorney?.isVerified
      //     ? null
      //     : uploadedFiles.Statutory_Reqs_Power_of_Attorney,
      //   Shops_Establishments_Act: pendingKycData?.kycDetails
      //     ?.Shops_Establishments_Act?.isVerified
      //     ? null
      //     : uploadedFiles.Shops_Establishments_Act,
      //   boardResolution: pendingKycData?.kycDetails?.boardResolution?.isVerified
      //     ? null
      //     : uploadedFiles.boardResolution,
      //   others: pendingKycData?.kycDetails?.others?.isVerified
      //     ? null
      //     : uploadedFiles.others,
      // },
      // urls: {
      //   financialEvaluation: uploadedFiles.fin_eval,
      // },
    };
    // if (!payload.docs.others) {
    //   delete payload.docs.others;
    // }
    // if (values.busType === "UTY1000003") {
    //   delete payload.docs.Certificate_of_Incorporation;
    //   delete payload.docs.kycPanDoc;
    //   delete payload.docs.KYC_Authorized_Signatory;
    //   delete payload.docs.Current_Shareholding_Pattern;
    //   delete payload.docs.Memorandum_and_Articles;
    //   delete payload.docs.Constitution_Documents;
    //   delete payload.docs.Statutory_Reqs_Power_of_Attorney;
    //   delete payload.docs.Shops_Establishments_Act;
    //   delete payload.docs.boardResolution;
    // }
    // if (values.busType === "UTY1000004") {
    //   delete payload.docs.Certificate_of_Incorporation;
    //   delete payload.docs.KYC_Authorized_Signatory;
    //   delete payload.docs.Current_Shareholding_Pattern;
    //   delete payload.docs.Memorandum_and_Articles;
    //   delete payload.docs.boardResolution;
    // }
    // if (values.busType === "UTY1000005") {
    //   delete payload.docs.Constitution_Documents;
    //   delete payload.docs.Statutory_Reqs_Power_of_Attorney;
    //   delete payload.docs.Shops_Establishments_Act;
    // }

    const result = await dispatch(
      token ? busKycSubmit(payload) : pendingKycSubmit(payload)
    );

    if (result.payload?.statusCode === 201) {
      message.success(
        ENQUIRY_SUCCESS_MESSAGES.KYC_SUBMITTED
      );
      if (token) {
        setIsSubmitted(true);
        dispatch(busGetPendingKyc({ orgId: organizationId, token }));
        dispatch(busKycHistory({ orgId: organizationId, token }));
      } else {
        setIsSubmitted(true);
        navigate(-1);
      }

    } else {
      message.error(
        result?.payload?.message || "Failed to submit KYC"
      );
    }
  };

  const handleSaveDraft = async () => {
    setDraftLoading(true);
    const values = form.getFieldsValue();
    const payload = {
      isDraft: true,
      token: token ? token : undefined,
      orgId: token ? organizationId : orgId,
      businessTypeId: values.busType,
      industryTypeId: values.industryType,
      hasMultipleGstin: values.hasMultipleGstin,
      annualTurnoverId: values.annualTurnover,
      dealCategoryId: values.dealType,
      expectedTenure: values.tenure ? Number(values.tenure) : undefined,
      expectedCredit: values.expAmt ? Number(values.expAmt) : undefined,
      incorporationDate: values.incorporationDate ? values.incorporationDate.format("YYYY-MM-DD") : undefined,
      busDescription: values.busDesc,
      website: values.busWebsite,
      financialSummary: values.finSummary,
      dirName: values.dirName,
      accNum: values.accNum,
      accHolderName: values.accHolderName,
      ifsc: values.ifsc,
      communicationAddress: buildCommunicationAddressPayload(values),
    };

    const result = await dispatch(
      token ? busKycSubmit(payload) : pendingKycSubmit(payload)
    );

    setDraftLoading(false);

    if (result.payload?.statusCode === 201) {
      message.success("KYC Draft saved successfully");
      if (token) {
        dispatch(busGetPendingKyc({ orgId: organizationId, token }));
        dispatch(busKycHistory({ orgId: organizationId, token }));
      } else {
        dispatch(
          getPendingKyc({
            orgId,
            dealCategoryId: routeDealCategoryId,
          })
        );
        dispatch(kycHistory(orgId));
      }
    } else {
      message.error(result?.payload?.message || "Failed to save KYC Draft");
    }
  };


  const isDirectorKycLink = searchParams.get("directorKyc") === "true";
  const isDirectorAadhaarVerified = mergedKycDetails?.kycDirectorsAadhaarValue?.isVerified;

  if (isExpired || (isDirectorKycLink && isDirectorAadhaarVerified)) {
    return (
      <div className="complete-kyc-container">
        <CustomCard className="complete-kyc-card" style={{ textAlign: "center", padding: "48px 24px" }}>
          <Result
            status="warning"
            title={isExpired ? "KYC Link Expired" : "Director KYC Already Verified"}
            subTitle={isExpired ? "This link is valid for 24 hours. If it expires, you will need to request a new one." : "The Director KYC has already been successfully verified. This link is no longer accessible."}
          />
        </CustomCard>
      </div>
    );
  }

  return (
    <div className="complete-kyc-container">
      {showKycSuccess ? (
        <KycSuccess
          isAlreadyCompleted={isAlreadyCompleted}
          onGotoDashboard={() => {
            window.location.assign(getBusinessPortalKycRedirectUrl());
          }}
        />
      ) : (
        <>
          <div className="kyc-page-header">
            <Text className="kyc-title">
              KYC Details for{" "}
              <span style={{ fontWeight: "bold" }}>{pendingKycData?.busName}</span>
            </Text>
            <Tag color={kycStatusColor} className="kyc-status-tag">
              {kycStatusLabel}
            </Tag>
          </div>
          <CustomCard className="complete-kyc-card">
            <Form layout="vertical" form={form} onFinish={handleSubmitKyc}>
              <div style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "16px",
              }}>
                <Text className="kyc-subtitle">{TITLE_TXT.BUS_DETS}</Text>
                {!isFieldReview && (!isPanVerified || !isGstinVerified) && (
                  <CustomButton
                    variant="outlined"
                    color="green"
                    htmlType="button"
                    onClick={handleVerifyBoth}
                    loading={panLoading || gstinLoading}
                  >
                    {`Verify ${isPanVerified ? "GSTIN" : "Both PAN & GSTIN"
                      }`}
                  </CustomButton>
                )}
              </div>
              {isReuseConfirmMode && (
                <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
                  KYC details are already completed for this business. Review the
                  information below, confirm, and submit for this deal category.
                </Text>
              )}
              <KycBusinessDetails
                kycData={mergedKycDetails?.kycPanValue}
                busPan={busPan}
                busName={busName}
                orgId={orgId}
                organizationId={organizationId}
                token={token}
                isReview={isFieldReview}
              />
              <KycContactDirector
                kycData={mergedKycDetails}
                email={email}
                phoneNo={phoneNo}
                orgId={orgId}
                form={form}
                organizationId={organizationId}
                token={token}
                dealCategoryId={formValues?.dealType || pendingKycData?.dealCategoryId}
                isReview={isFieldReview}
              />
              <KycBank
                kycData={mergedKycDetails}
                ifsc={ifsc}
                accNum={accNum}
                accHolderName={accHolderName}
                orgId={orgId}
                form={form}
                organizationId={organizationId}
                token={token}
                isReview={isFieldReview}
              />
              <Text className="kyc-subtitle">{TITLE_TXT.ADD_VERIFY}</Text>
              <KycAddress
                kycData={mergedKycDetails?.kycGstinValue}
                gstin={gstin}
                form={form}
                orgId={orgId}
                organizationId={organizationId}
                token={token}
                isReview={isFieldReview}
              />
              <KycAdditionalInfo
                kycData={mergedKycDetails?.kycDirectorsAadhaarValue}
                orgId={orgId}
                form={form}
                organizationId={organizationId}
                token={token}
                isReview={isFieldReview}
                isNarrativeReview={
                  isFieldReview &&
                  Boolean(String(pendingKycData?.busDescription || "").trim()) &&
                  Boolean(String(pendingKycData?.financialSummary || "").trim())
                }
              />
              {!isReadOnly && (
                <>
                  <Form.Item style={{ marginTop: 20 }}>
                    <Checkbox
                      checked={isTermsAccepted}
                      onChange={(e) => setIsTermsAccepted(e.target.checked)}
                    >
                      I confirm that I have verified and uploaded all the information
                      provided for the company {busName}.
                    </Checkbox>
                  </Form.Item>
                  <FormFooter
                    onCancelClick={() => {
                      navigate(-1);
                      form.resetFields();
                    }}
                    noCancel={token ? true : false}
                    error={error || panError || gstinError}
                    submitLoading={pendingKycSubmitLoading}
                    btnName={BTN_TXT.SUBMIT}
                    disabled={!isTermsAccepted}
                    draftBtn={isReuseConfirmMode ? undefined : "Save as Draft"}
                    onDraftClick={isReuseConfirmMode ? undefined : handleSaveDraft}
                    draftLoading={draftLoading}
                  />
                </>
              )}
              {isReadOnly && !token && (
                <Flex justify="flex-end" style={{ marginTop: 24 }}>
                  <CancelButton onClick={() => navigate(-1)}>
                    Back to Enquiry Management
                  </CancelButton>
                </Flex>
              )}
            </Form>
          </CustomCard>
        </>
      )}
    </div>
  );
};

export default CompleteKyc;
